import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
	EmptyStateComponent,
	ITagIcon,
	ItemComponent,
	LinkComponent,
	ListDetailComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import {
	CHECK,
	IOperationEvaluationDetail,
	IProcessDetailResponse,
	PROCESS_STATUS,
} from '../../../../../processes/models/processes.models';
import { ChecksComponent } from '../../../../../processes/components/checks/checks.component';
import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
	IOperationDetailResponse,
	OPERATION_STATUS,
} from '../../../../models/operations.models';
import { OperationDetailTabsChecksFinancialEvaluationBankModalComponent } from './bank-modal/bank-modal.component';
import { OperationDetailTabsChecksFinancialEvaluationIrpfModalComponent } from './irpf-modal/irpf-modal.component';
import { NOT_AVAILABLE_DISPLAY } from '../../../../../core/utils/utils';

@Component({
	selector: 'app-operation-detail-tabs-checks-financial-evaluation',
	imports: [
		TranslatePipe,
		TileComponent,
		EmptyStateComponent,
		ChecksComponent,
		ItemComponent,
		LinkComponent,
		ListDetailComponent,
		OperationDetailTabsChecksFinancialEvaluationBankModalComponent,
		OperationDetailTabsChecksFinancialEvaluationIrpfModalComponent,
	],
	templateUrl: './operation-detail-tabs-checks-financial-evaluation.component.html',
	styleUrl: './operation-detail-tabs-checks-financial-evaluation.component.scss',
})
export class OperationDetailTabsChecksFinancialEvaluationComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	public checksDetail: IProcessDetailResponse | null = null;
	public evaluationData: IOperationEvaluationDetail | null = null;
	public evaluatedIban = '';
	public registryIban = '';
	public isBankDetailsModalVisible = false;
	public isIrpfDetailsModalVisible = false;
	public irpfDetailsPayload: unknown = null;
	public CHECK = CHECK;
	public EVALUATION_TYPE = EVALUATION_TYPE;
	public CONNECTION_TYPE = CONNECTION_TYPE;

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.evaluationData = this.detail?.data
			? (this.detail.data as unknown as IOperationEvaluationDetail)
			: null;
		this.checksDetail = this.evaluationData ? this.buildChecksDetail(this.evaluationData) : null;
		const ibanValues = this.resolveBankScoringIbans();
		this.evaluatedIban = ibanValues.evaluated;
		this.registryIban = ibanValues.registry;
		this.irpfDetailsPayload = this.resolveIrpfDetailsPayload();
	}

	public get isBankScoringMatchData(): boolean {
		return (
			this.evaluationData?.type === CONNECTION_TYPE.BANK_SCORING &&
			this.evaluationData?.evaluation === EVALUATION_TYPE.MATCH_DATA
		);
	}

	public getTagIconItem(): ITagIcon {
		if (this.evaluationData?.state === OPERATION_STATUS.OK) {
			return { icon: 'icon-check', status: 'success', size: 'sm' };
		}
		return { icon: 'icon-alert', status: 'error', size: 'sm' };
	}

	public get isIrpfMatchData(): boolean {
		return this.evaluationData?.type === CONNECTION_TYPE.IRPF && this.evaluationData?.evaluation === EVALUATION_TYPE.MATCH_DATA;
	}

	public get isWorkLifeType(): boolean {
		const type = this.evaluationData?.type?.toUpperCase();
		return type === CONNECTION_TYPE.WORK_LIFE || type === 'WORK_LIFE';
	}

	public openBankDetailsModal(event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();
		this.isBankDetailsModalVisible = true;
	}

	public closeBankDetailsModal(): void {
		this.isBankDetailsModalVisible = false;
	}

	public openIrpfDetailsModal(event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();
		this.isIrpfDetailsModalVisible = true;
	}

	public closeIrpfDetailsModal(): void {
		this.isIrpfDetailsModalVisible = false;
	}

	public maskIban(value?: string): string {
		const raw = (value ?? '').replace(/\s+/g, '');
		if (!raw) return NOT_AVAILABLE_DISPLAY;
		if (raw.length <= 4) return raw;
		return `**** **** **** ${raw.slice(-4)}`;
	}

	private buildChecksDetail(evaluation: IOperationEvaluationDetail): IProcessDetailResponse {
		return {
			general: {
				profile: { uuid: '', alias: '' },
				sms: [],
				auth: '',
				company: '',
				company_uuid: '',
				location: [],
				devices: [],
				init_operation: '',
				total_time: 0,
				end_operation: '',
				validation: '',
			},
			process_uuid: this.detail?.process_uuid ?? '',
			process_details: '',
			status_process: PROCESS_STATUS.OK,
			data: { EVALUATION: evaluation } as IProcessDetailResponse['data'],
			locks: this.detail?.locks,
		} as IProcessDetailResponse;
	}

	private resolveBankScoringIbans(): { evaluated: string; registry: string } {
		const raw = this.detail?.data as Record<string, unknown> | undefined;
		const detailPayload = this.parseMaybeJson(raw?.['details']);
		const candidates: unknown[] = [raw, detailPayload];

		const evaluated = this.pickByKeys(candidates, [
			'evaluated_iban',
			'connection_iban',
			'bank_iban',
			'iban',
			'value',
		]);

		const registry = this.pickByKeys(candidates, ['registry_iban', 'expected_iban', 'data_iban', 'identifier', 'data']);

		return {
			evaluated: this.stringifySimple(evaluated),
			registry: this.stringifySimple(registry),
		};
	}

	private pickByKeys(objects: unknown[], keys: string[]): unknown {
		for (const candidate of objects) {
			if (!candidate || typeof candidate !== 'object') continue;
			const entry = candidate as Record<string, unknown>;
			for (const key of keys) {
				const value = entry[key];
				if (value !== undefined && value !== null && value !== '') {
					return value;
				}
			}
		}
		return '';
	}

	private parseMaybeJson(value: unknown): Record<string, unknown> | null {
		if (typeof value !== 'string') return null;
		try {
			const parsed = JSON.parse(value) as unknown;
			if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
		} catch {
			return null;
		}
		return null;
	}

	private stringifySimple(value: unknown): string {
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			return String(value);
		}
		if (value && typeof value === 'object') {
			const obj = value as Record<string, unknown>;
			for (const key of ['text', 'value', 'iban', 'id']) {
				const field = obj[key];
				if (typeof field === 'string' || typeof field === 'number') {
					return String(field);
				}
			}
		}
		return '';
	}

	private resolveIrpfDetailsPayload(): unknown {
		if (!this.evaluationData) return null;
		const rawDetails = this.evaluationData.details ?? this.detail?.details;
		if (rawDetails !== undefined && rawDetails !== null) return rawDetails;
		const firstDocument = this.evaluationData.document?.[0];
		return firstDocument?.details ?? firstDocument?.data ?? null;
	}
}
